/**
 * Remind PA: the button, the dialog and the state on a TWL pipeline row (docs/provider/REMIND_PA.md).
 *
 * The button shows only where the server says this provider may remind the PA about this resident (row.remind). The
 * dialog asks the server first (the preview): who it goes to -- a ROLE, never a Patient Advocate's name -- or why it
 * cannot go now. An optional note of at most 300 characters is shown to the PA in eMerge only, never emailed. After
 * sending, the row says "Reminded <date>" and until when it stays open; the server keeps the rate limits and says when
 * another may be sent.
 */
import React, { useEffect, useRef, useState } from "react";
import { BellRing, Check } from "lucide-react";
import { Button } from "../../../components/shadcn/ui/button";
import { Textarea } from "../../../components/shadcn/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/shadcn/ui/dialog";
import { NOTE_MAX, newReminderKey, paReminderApi } from "./paReminderApi";
import type { RemindPreview, SentReminder, TwlRemind } from "./paReminderApi";

/** A day, as the provider's browser shows it ("Oct 27"). Display only: nothing is compared in a time zone here. */
export function dayWords(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
/** A day and a time ("Oct 28, 9:00 AM"), for "again after". */
export function whenWords(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

/** When the next reminder may go, while a limit applies (other than the open one, which says its own date). */
function limitWords(r: TwlRemind): string | null {
  if (r.limit === "RESIDENT_RECENTLY_REMINDED") return `the Patient Advocate team was reminded in the last 24 hours · again after ${whenWords(r.next_at)}`;
  if (r.limit === "DAILY_LIMIT") return `you have sent 3 reminders in the last 24 hours · again after ${whenWords(r.next_at)}`;
  return null;
}

/**
 * The state line beside the button, in words (ruling 5 and verifier D1: one reading, the same as the server's). The
 * provider's own latest reminder says its status and its date -- open until, answered on, or expired unanswered on --
 * and never who answered or what they recorded. Null when there is nothing to say.
 */
export function remindStateWords(r: TwlRemind): string | null {
  const limit = limitWords(r);
  const cap = (x: string) => x.charAt(0).toUpperCase() + x.slice(1);
  if (r.open || r.status === "OPEN") {
    return `Reminded ${dayWords(r.last_at)} · open until ${dayWords(r.open_until || r.status_at)}, or until a Patient Advocate records a note or a contact touch`;
  }
  if (r.last_at && r.status === "ANSWERED") return `Reminded ${dayWords(r.last_at)} · answered ${dayWords(r.status_at)}${limit ? ` · ${limit}` : ""}`;
  if (r.last_at && r.status === "EXPIRED") {
    // "The PA managers were told" only when they were (an escalation row exists: verifier, round 2), never from the clock.
    return `Reminded ${dayWords(r.last_at)} · no answer by ${dayWords(r.status_at)}${r.escalated ? "; the PA managers were told" : ""}${limit ? ` · ${limit}` : ""}`;
  }
  if (limit) return cap(limit);
  if (r.last_at) return `Reminded ${dayWords(r.last_at)}`;
  return null;
}

/** A stage the provider cannot remind about, said plainly where it helps (ruling 8: an unrecognised status is the office's). */
export function remindHint(r: TwlRemind | undefined): string | null {
  if (!r) return null;
  if (r.not_available === "STAGE_UNRECOGNISED") return "Status not recognised: tell the office, a reminder would not reach the right person.";
  return null;
}

type Preview =
  | { kind: "loading" }
  | { kind: "ok"; data: RemindPreview }
  | { kind: "failure"; message: string };

type Outcome =
  | { kind: "idle" }
  | { kind: "sending" }
  | { kind: "sent"; data: SentReminder }
  | { kind: "refused"; message: string; retryAt: string | null };

export interface RemindPaProps {
  facilityId: string;
  patientId: string;
  residentName: string;
  remind?: TwlRemind;
  /** The row's new state after a send, so the list shows it without a reload. */
  onSent?: (next: TwlRemind) => void;
}

export default function RemindPa({ facilityId, patientId, residentName, remind, onSent }: RemindPaProps) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState<Preview>({ kind: "loading" });
  const [note, setNote] = useState("");
  const [outcome, setOutcome] = useState<Outcome>({ kind: "idle" });
  const key = useRef(newReminderKey());
  const live = useRef(true);
  useEffect(() => () => { live.current = false; }, []);

  useEffect(() => {
    if (!open) return;
    setPreview({ kind: "loading" });
    paReminderApi.preview(facilityId, patientId).then((res) => {
      if (!live.current) return;
      if (res.success && res.data) setPreview({ kind: "ok", data: res.data });
      else setPreview({ kind: "failure", message: res.message || res.error || "Could not check who the reminder would go to." });
    }).catch(() => { if (live.current) setPreview({ kind: "failure", message: "The server could not be reached." }); });
  }, [open, facilityId, patientId]);

  // Not offered here: a stage past the PA, a closed building, no edit rights, not a provider relationship, not in the
  // pipeline, or reminders not switched on yet (the server says which). An unrecognised status says to tell the office.
  if (!remind) return null;
  const hint = remindHint(remind);
  if (hint) return <div className="fw-twl-remind"><span className="fw-twl-remind-state">{hint}</span></div>;
  if (remind.not_available === "NOT_READY" || (remind.not_available && !remind.last_at)) return null;
  const words = remindStateWords(remind);
  const offered = remind.available && !remind.not_available;

  const start = () => {
    key.current = newReminderKey();
    setNote("");
    setOutcome({ kind: "idle" });
    setOpen(true);
  };

  const send = async () => {
    setOutcome({ kind: "sending" });
    const res = await paReminderApi.send(facilityId, patientId, note, key.current);
    if (!live.current) return;
    if (res.success && res.data) {
      const sent = res.data;
      setOutcome({ kind: "sent", data: sent });
      if (onSent) {
        onSent({
          available: false, not_available: null, last_at: sent.created_at, status: "OPEN", status_at: sent.expires_at,
          open: true, open_until: sent.expires_at, limit: "ALREADY_REMINDED", next_at: sent.expires_at,
        });
      }
      return;
    }
    const retryAt = res.status === 429 && res.retryAfterSeconds ? new Date(Date.now() + res.retryAfterSeconds * 1000).toISOString() : null;
    // A refusal is final for this key: the next attempt is a new one.
    key.current = newReminderKey();
    setOutcome({ kind: "refused", message: res.message || res.error || "The reminder could not be sent.", retryAt });
  };

  const p = preview.kind === "ok" ? preview.data : null;
  const blocked = p ? (!p.can_remind ? (p.refusal ? p.refusal.message : p.limit ? p.limit.message : "A reminder cannot be sent now.") : null) : null;
  const blockedAt = p && p.limit ? p.limit.retry_at : null;
  const tooLong = note.trim().length > NOTE_MAX;

  return (
    <div className="fw-twl-remind">
      {offered && (
        <Button variant="outline" type="button" className="fw-twl-action fw-twl-remind-button" onClick={start}
          aria-label={`Remind the Patient Advocate about ${residentName}`}>
          <BellRing aria-hidden="true" />
          Remind PA
        </Button>
      )}
      {words && <span className={`fw-twl-remind-state${remind.open ? " open" : ""}`}>{remind.open && <Check aria-hidden="true" />}{words}</span>}

      <Dialog open={open} onOpenChange={(v) => { if (outcome.kind !== "sending") setOpen(v); }}>
        <DialogContent className="fw-twl-remind-dialog" aria-describedby="fw-remind-desc">
          <DialogHeader>
            <DialogTitle>Remind the Patient Advocate</DialogTitle>
            <DialogDescription id="fw-remind-desc">
              Ask the Patient Advocate team to follow up on {residentName}. It moves the case up their work list until a
              Patient Advocate records a note or a contact touch on it{p ? `, or for ${p.expiry_days} days` : ""}.
            </DialogDescription>
          </DialogHeader>

          <div aria-live="polite" className="fw-twl-remind-body">
            {preview.kind === "loading" && <p className="fw-muted">Checking who this goes to…</p>}
            {preview.kind === "failure" && <p role="alert">{preview.message}</p>}
            {p && p.last && p.last.status && outcome.kind !== "sent" && (
              <p className="fw-muted">
                Your last reminder ({dayWords(p.last.at)}):{" "}
                {p.last.status === "OPEN" ? `open until ${dayWords(p.last.status_at)}`
                  : p.last.status === "ANSWERED" ? `answered ${dayWords(p.last.status_at)}`
                    : `no answer by ${dayWords(p.last.status_at)}${p.last.escalated ? "; the PA managers were told" : ""}`}.
              </p>
            )}
            {p && p.recipient && !blocked && outcome.kind !== "sent" && (
              <p className="fw-twl-remind-to">This goes to <strong>{p.recipient.words}</strong>.</p>
            )}
            {blocked && outcome.kind !== "sent" && (
              <p role="alert" className="fw-twl-remind-blocked">
                {blocked}{blockedAt ? ` You can send another after ${whenWords(blockedAt)}.` : ""}
              </p>
            )}
            {outcome.kind === "sent" && (
              <p role="status" className="fw-twl-remind-sent">
                <Check aria-hidden="true" /> Sent to {outcome.data.recipient.words}. It stays open until a Patient Advocate records
                a note or a contact touch on the case, or until {dayWords(outcome.data.expires_at)}.
              </p>
            )}
            {outcome.kind === "refused" && (
              <p role="alert" className="fw-twl-remind-blocked">
                {outcome.message}{outcome.retryAt ? ` You can send another after ${whenWords(outcome.retryAt)}.` : ""}
              </p>
            )}
          </div>

          {outcome.kind !== "sent" && p && p.can_remind && (
            <label className="fw-twl-remind-note">
              <span>Note for the Patient Advocate (optional)</span>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={NOTE_MAX + 50}
                placeholder="For example: the daughter asked about hearing aids at today's visit." />
              <small className={tooLong ? "over" : undefined}>
                {note.trim().length}/{NOTE_MAX} · shown to the Patient Advocate in eMerge only, never emailed
              </small>
            </label>
          )}

          <DialogFooter>
            {outcome.kind === "sent" ? (
              <Button type="button" onClick={() => setOpen(false)}>Done</Button>
            ) : (
              <>
                <Button variant="outline" type="button" onClick={() => setOpen(false)} disabled={outcome.kind === "sending"}>Cancel</Button>
                <Button type="button" onClick={send} disabled={!p || !p.can_remind || tooLong || outcome.kind === "sending"}>
                  {outcome.kind === "sending" ? "Sending…" : "Send reminder"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Remind PA where a screen holds one resident (the resident workbench, ruling 7): asks the server for this resident's
 * `remind` state (the same object a TWL pipeline row carries), then draws the same button, dialog and state. Draws
 * nothing while it asks, when the server has nothing to offer, or when the read fails.
 */
export function RemindPaFor({ facilityId, patientId, residentName }: { facilityId: string; patientId: string; residentName: string }) {
  const [remind, setRemind] = useState<TwlRemind | undefined>(undefined);
  useEffect(() => {
    let live = true;
    paReminderApi.state([{ facilityId, patientId }]).then((res) => {
      if (!live || !res.success || !res.data) return;
      const item = res.data.items.find((i) => i.patient_id.toLowerCase() === patientId.toLowerCase());
      setRemind(item ? item.remind : undefined);
    }).catch(() => {});
    return () => { live = false; };
  }, [facilityId, patientId]);
  if (!remind) return null;
  return <RemindPa facilityId={facilityId} patientId={patientId} residentName={residentName} remind={remind} onSent={setRemind} />;
}
